import { useMemo } from "react";
import {
  DocumentStages,
  DocumentStatus,
  Result,
  TransactionalEmailEvents,
} from "../../../../../../graphql/__GENERATED__/types";
import useDocumentDetails from "../../../../../../hooks/useDocumentDetails";
import { useUIStore } from "../../../../../../hooks/useUIStore";
import useConfirmedAction from "../../../../../../hooks/useConfirmedAction";
import { IconNames } from "../../../../../../components/Icons/styles/iconNames";
import useDocumentOrder from "../../../../../../hooks/useDocumentOrder";

const useQuotationFooter = () => {
  const { openSubmittedModal, closeSubmittedModal } = useUIStore();

  const {
    orderedResults,
    setOrderedResults,
    quotationComment,
    handleDownloadComparisonPdf,
    handleChangeDocumentStage,
    handleFinishQuotation,
    handleRedoQuotations,
    handleSetDocumentsAsSent,
    handleSendEmail,
    documentData: { stage, status, selectedSimulationResults },
  } = useDocumentDetails();

  const showSentQuotationsMessage = () => {
    openSubmittedModal("Cotações enviadas com sucesso!");
    const interval = setTimeout(() => {
      closeSubmittedModal();
    }, 3000);
    return () => clearInterval(interval);
  };

  const saveSelectedQuotations = async () => {
    await handleSendEmail(TransactionalEmailEvents.QuotationResults);
    showSentQuotationsMessage();
  };

  const recedeToPending = useConfirmedAction(
    "Tem certeza? O documento retornará ao status de COTAÇÃO PENDENTE.",
    async () => {
      await handleChangeDocumentStage(DocumentStatus.Pending);
    }
  );

  const recedeToDone = useConfirmedAction(
    "Tem certeza? O documento retornará ao status de COTAÇÃO FINALIZADA.",
    async () => {
      // save ordered results before cleaning selected results
      setOrderedResults(selectedSimulationResults as Result[]);
      await handleChangeDocumentStage(DocumentStatus.Done);
    }
  );

  const currentStageStatus = useDocumentOrder(stage!, status!);
  const quotationPending = useDocumentOrder(
    DocumentStages.Simulation,
    DocumentStatus.Pending
  );
  const quotationDone = useDocumentOrder(
    DocumentStages.Simulation,
    DocumentStatus.Done
  );
  const policyStage = useDocumentOrder(DocumentStages.Policy);

  return useMemo(() => {
    if (currentStageStatus >= policyStage) {
      return [];
    }
    if (currentStageStatus === quotationPending) {
      return [
        {
          text: "Recalcular Cotações",
          onClick: () => handleRedoQuotations(),
          iconName: IconNames.Redo,
        },
        {
          text: "Finalizar cotação",
          disabled: !quotationPending,
          onClick: () => handleFinishQuotation(),
          iconName: IconNames.Valid,
        },
      ];
    }
    if (currentStageStatus === quotationDone) {
      return [
        {
          text: "Editar",
          onClick: recedeToPending,
          iconName: IconNames.Edit,
        },
        {
          text: "Enviar cotação",
          disabled: !orderedResults.length,
          onClick: saveSelectedQuotations,
          iconName: IconNames.Sent,
        },
        {
          text: "Marcar como Enviada",
          onClick: handleSetDocumentsAsSent,
          iconName: IconNames.Sent,
        },
        {
          text: "Baixar tabela",
          onClick: handleDownloadComparisonPdf,
          iconName: IconNames.Download,
          disabled: !orderedResults.length,
        },
      ];
    } else {
      return [
        {
          text: "Editar",
          onClick: recedeToDone,
          iconName: IconNames.Edit,
        },
        {
          text: "Baixar tabela",
          onClick: handleDownloadComparisonPdf,
          iconName: IconNames.Download,
        },
      ];
    }
  }, [stage, status, orderedResults, quotationComment]);
};

export default useQuotationFooter;
